import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Col, Descriptions, Divider, Grid, List, message, Popconfirm, Row, Select, Space} from 'antd';
import {DeleteOutlined, EditOutlined, UserAddOutlined} from '@ant-design/icons';
import {addProjectMember, listProjectMembers, removeProjectMember} from '../../api/projectMemberApi';
import userApi from '../../api/userApi';
import PermissionButton from '../common/PermissionButton';
import PermissionGate from '../common/PermissionGate';
import {deleteProject, updateProject} from '../../api/projectApi';

// props: { project, onEdit, onDelete }
const ProjectInfoTab = ({ project, onEdit, onDelete }) => {
  const screens = Grid.useBreakpoint();
  const isXs = !screens.md;

  // 상태
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState();

  // 안전한 pjtId 추출 (Hook 이후에 사용)
  const pjtId = project?.pjtId;

  const userMap = useMemo(() => {
    const m = new Map();
    (users || []).forEach(u => m.set(u.userId, u));
    return m;
  }, [users]);

  const memberUserIdSet = useMemo(() => new Set((members || []).map(m => m.userId)), [members]);

  // 사용자 목록 조회
  const fetchUsers = useCallback(async () => {
    try {
      const res = await userApi.listUsers(); // ApiResponse 형식
      const raw = res?.data ?? res; // 혹시 레이어가 바뀌어도 안전하게 처리
      const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
      setUsers(list);
    } catch (_) { /* 글로벌 인터셉터 처리 */ }
  }, []);

  // 프로젝트 멤버 조회
  const fetchMembers = useCallback(async () => {
    if (!pjtId) return;
    setLoading(true);
    try {
      const res = await listProjectMembers(pjtId); // ApiResponse
      const raw = res?.data ?? res;
      const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
      setMembers(list);
    } finally {
      setLoading(false);
    }
  }, [pjtId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // 멤버 추가
  const handleAdd = async () => {
    if (!pjtId || !selectedUserId) return;
    setAdding(true);
    try {
      await addProjectMember(pjtId, { userId: selectedUserId });
      message.success('멤버가 추가되었습니다.');
      setSelectedUserId(undefined);
      fetchMembers();
    } catch (_) { /* 글로벌 처리 */ }
    finally { setAdding(false); }
  };

  // 멤버 삭제
  const handleRemove = async (pjtMbrId) => {
    if (!pjtId || !pjtMbrId) return;
    try {
      await removeProjectMember(pjtId, pjtMbrId);
      message.success('멤버가 제거되었습니다.');
      fetchMembers();
    } catch (_) { /* 글로벌 처리 */ }
  };

  // project 없을 때 렌더 중단 (Hook 이후)
  if (!project) return null;

  return (
    <div>
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }} align="middle">
        <Col span={24}>
          <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
            <PermissionButton danger size={isXs ? 'small' : 'middle'} icon={<DeleteOutlined />} onClick={() => onDelete && onDelete(project)} require={deleteProject}>
              {isXs ? '삭제' : '프로젝트 삭제'}
            </PermissionButton>
            <PermissionButton size={isXs ? 'small' : 'middle'} type="primary" icon={<EditOutlined />} onClick={onEdit} require={updateProject}>
              {isXs ? '수정' : '프로젝트 수정'}
            </PermissionButton>
          </Space>
        </Col>
      </Row>

      <Descriptions bordered size="middle" column={{ xs: 1, sm: 1, md: 2 }}>
        <Descriptions.Item label="프로젝트명">{project.pjtNm}</Descriptions.Item>
        <Descriptions.Item label="프로젝트 번호">{project.pjtNo || '-'}</Descriptions.Item>
        <Descriptions.Item label="담당자명">{project.pjtPicNm || '-'}</Descriptions.Item>
        <Descriptions.Item label="개방일시">{project.pjtOpnDt || '-'}</Descriptions.Item>
        <Descriptions.Item label="시작일자">{project.pjtBgngYmd || '-'}</Descriptions.Item>
        <Descriptions.Item label="종료일자">{project.pjtEndYmd || '-'}</Descriptions.Item>
        <Descriptions.Item label="등록일시">{project.regDt || '-'}</Descriptions.Item>
        <Descriptions.Item label="등록자">{project.rgtr || '-'}</Descriptions.Item>
        <Descriptions.Item label="수정일시">{project.mdfcnDt || '-'}</Descriptions.Item>
        <Descriptions.Item label="수정자">{project.mdfr || '-'}</Descriptions.Item>
        <Descriptions.Item label="프로젝트 정보" span={2}>{project.pjtInfo || '-'}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" style={{ marginTop: 24 }}>프로젝트 멤버</Divider>

      <PermissionGate require={listProjectMembers}>
        {({allowed}) => (
          <div style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
            <Row gutter={[12,12]} align="middle" style={{ marginBottom: 12 }}>
              <Col flex="auto">
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="추가할 사용자 선택"
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={(users||[]).map(u => ({
                    value: u.userId,
                    label: u.userNm || u.lgnUserNm || u.eml || String(u.userId),
                    disabled: memberUserIdSet.has(u.userId)
                  }))}
                />
              </Col>
              <Col>
                <PermissionButton type="primary" icon={<UserAddOutlined />} onClick={handleAdd} loading={adding} disabled={!selectedUserId} require={addProjectMember}>
                  추가
                </PermissionButton>
              </Col>
            </Row>

            <List
              bordered
              loading={loading}
              dataSource={members}
              locale={{ emptyText: '등록된 멤버가 없습니다.' }}
              rowKey={(m) => m.pjtMbrId || `${m.userId}`}
              renderItem={(m) => {
                const u = userMap.get(m.userId);
                const name = u?.userNm || u?.lgnUserNm || String(m.userId);
                const sub = u?.eml;
                return (
                  <List.Item
                    actions={[
                      <Popconfirm
                        key="del"
                        title="멤버 삭제"
                        description={`${name} 사용자를 제거하시겠습니까?`}
                        okText="삭제"
                        okType="danger"
                        cancelText="취소"
                        onConfirm={() => handleRemove(m.pjtMbrId)}
                      >
                        <PermissionButton danger size="small" icon={<DeleteOutlined />} require={removeProjectMember}>삭제</PermissionButton>
                      </Popconfirm>
                    ]}
                  >
                    <List.Item.Meta title={name} description={sub} />
                  </List.Item>
                );
              }}
            />
          </div>
        )}
      </PermissionGate>
    </div>
  );
};

export default ProjectInfoTab;
